import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Button
} from '@mui/material';
import { Add as AddIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { groupService } from '../../services/groupService';
import GroupDetail from './GroupDetail';

const GroupList = () => {
  const { project } = useParams();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const data = await groupService.getGroups(project);
      setGroups(data);
      if (data.length > 0 && !selectedGroup) {
        setSelectedGroup(data[0]);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Failed to load groups.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project) {
      fetchGroups();
    }
  }, [project]);

  const handleAddNew = () => {
    setSelectedGroup(null); // null selection triggers the "Add New" form
  };

  const handleSaveSuccess = async (savedGroup, isNew) => {
    await fetchGroups(); // Refresh list
    if (isNew) {
      // auto-select the newly created one (might not match exactly if no id is returned, handle safely)
      setSelectedGroup(savedGroup);
    }
  };

  if (loading && groups.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Groups
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          Add Group
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left Side: Group List Table */}
        <Grid item xs={12} md={7}>
          <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">No groups found</TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => (
                    <TableRow
                      key={group._id}
                      hover
                      onClick={() => setSelectedGroup(group)}
                      selected={selectedGroup?._id === group._id}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Side: Group Detail/Form */}
        <Grid item xs={12} md={5}>
          <GroupDetail
            group={selectedGroup}
            projectId={project}
            onSaveSuccess={handleSaveSuccess}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default GroupList;
